import { enhancedApi as generatedBillingV2Api } from '@store/generated/billing-v2';
import type { RootState } from '@store/types';

import { selectSignedInName, selectSignedInPrincipalId } from '../../user/store/selectors';
import type {
  EncounterWorkDispositionRequest,
  EncounterWorkDispositionResponse,
  EncounterWorkNote,
  EncounterWorkNoteRequest,
  EncounterWorkNoteResponse,
  EncounterWorkNotesResponse,
} from '../types/encounterWork';

import { makeProvisionalNote } from './provisionalNote';

// Re-export all generated types for convenience
export type {
  AccountActivityRowView,
  AccountActivitySourceView,
  ActivityFeedEvent,
  ActivityFeedGroup,
  AssignChargeToEncounterRequest,
  AssignChargeToEncounterResponse,
  AttentionDisposition,
  AttentionLane,
  BillingOverviewResponse,
  BillingPayerOptionRow,
  BillingPayerOptionsResponse,
  BillingSavedView,
  BillingSavedViewListResponse,
  ChargeLineItem,
  ChargeLinePreviewEncounter,
  ChargeLinePreviewLine,
  ChargeLinePreviewRequest,
  ChargeLinePreviewResponse,
  ClaimCase,
  ClaimCaseDetailResponse,
  ClaimCaseListItem,
  ClaimCaseListResponse,
  ClaimCaseReviewDetail,
  ClaimCaseReviewResult,
  ClaimCertificationResponse as ClaimCertification,
  CertificationCountSummary as ClaimCertificationCountSummary,
  CertificationDispositionItem as ClaimCertificationDisposition,
  DispositionCommandRequest as ClaimCertificationDispositionCommand,
  DispositionCommandResponse as ClaimCertificationDispositionCommandResult,
  DispositionConflictResponse as ClaimCertificationDispositionConflict,
  CertificationFindingItem as ClaimCertificationFinding,
  CertificationFindingAnchorItem as ClaimCertificationFindingAnchor,
  CertificationFindingEffectItem as ClaimCertificationFindingEffect,
  CertificationWarning as ClaimCertificationWarning,
  ClaimsV2Response,
  ClaimV2Item,
  ClaimWorkCounts,
  ClaimWorkFilters,
  ClaimWorkFindingHighlight,
  ClaimWorkItem,
  ClaimWorkListResponse,
  ClaimWorkSavedConfig,
  ClaimWorkSortKey,
  ClaimWorkViewKey,
  CreditAllocationRequest,
  CreditAllocationResponse,
  EncounterAdjustmentsSplit,
  EncounterClaimSummary,
  EncounterLedgerGroup,
  EraTriage,
  FlowHealthRow,
  GenerateClaimCaseReviewResponse,
  LedgerEntryItem,
  PatientBillingQueueResponse,
  PatientBillingQueueSummary,
  PatientBillingStatus,
  PatientDueStatus,
  PatientFinancialSummaryResponse,
  PatientFinancialSummaryTotals,
  PatientInvoiceHistoryItem,
  PatientQueueItem,
  PatientRefundRequest,
  PatientRefundResponse,
  PatientResponsibilityBreakdown,
  RefundablePaymentItem,
  RefundablePaymentsResponse,
  RemittancesV2Response,
  RemittanceV2Item,
  SegmentCount,
  SentInvoiceItem,
  SentInvoicesResponse,
  SentInvoicesSummary,
  SummaryStrip,
  WhatToDo,
  WorkGroup,
  WorkItem,
  WorkQueueResponse,
} from '@store/generated/billing-v2';

// Cache tag constants
const V2_TAGS = {
  OVERVIEW: 'BillingV2Overview',
  WORK: 'BillingV2Work',
  CLAIMS: 'BillingV2Claims',
  CLAIM_CASES: 'BillingV2ClaimCases',
  REMITTANCES: 'BillingV2Remittances',
  PATIENT_BILLING: 'BillingV2PatientBilling',
  PATIENT_DETAIL: 'BillingV2PatientDetail',
  CLAIM_CERTIFICATION: 'BillingV2ClaimCertification',
  CLAIM_WORK: 'BillingV2ClaimWork',
  CLAIM_WORK_SAVED_VIEWS: 'BillingV2ClaimWorkSavedViews',
  /**
   * The counts half of the work page (GDN-3380). Separate from CLAIM_WORK
   * because Home asks for counts on their own — one `get_claim_work_counts`
   * call beside four page calls — and a verb has to refresh the badge even
   * when no page query for that queue is mounted.
   */
  CLAIM_WORK_COUNTS: 'BillingV2ClaimWorkCounts',
  /** One encounter's work notes, keyed by encounter id. */
  ENCOUNTER_WORK_NOTES: 'BillingV2EncounterWorkNotes',
} as const;

// Enhance generated API with cache tags
const billingV2Api = generatedBillingV2Api.enhanceEndpoints({
  addTagTypes: Object.values(V2_TAGS),
  endpoints: {
    getV2ProviderBillingV2Overview: {
      providesTags: [V2_TAGS.OVERVIEW],
    },
    getV2ProviderBillingV2Work: {
      providesTags: [V2_TAGS.WORK],
    },
    getV2ProviderBillingV2Claims: {
      providesTags: [V2_TAGS.CLAIMS],
    },
    getV2ProviderBillingV2ClaimWork: {
      /**
       * BOTH TAGS, because this one response is both halves. The rows are the
       * page; `counts` on the same body is what Home's queue badges read
       * (contract section 10 calls it `get_claim_work_counts`, but there is no
       * second route — the RPC's result rides here). A verb that changed a
       * count without changing this page's rows still has to refresh the
       * badges, and `CLAIM_WORK_COUNTS` is how it says so.
       */
      providesTags: [V2_TAGS.CLAIM_WORK, V2_TAGS.CLAIM_WORK_COUNTS],
    },
    getV2ProviderBillingV2ClaimWorkSavedViews: {
      providesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    postV2ProviderBillingV2ClaimWorkSavedViews: {
      invalidatesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    putV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewId: {
      invalidatesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    deleteV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewId: {
      invalidatesTags: [V2_TAGS.CLAIM_WORK_SAVED_VIEWS],
    },
    getV2ProviderBillingV2ClaimCases: {
      providesTags: [V2_TAGS.CLAIM_CASES],
    },
    getV2ProviderBillingV2ClaimCasesByClaimId: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.CLAIM_CASES, id: arg.claimId },
      ],
    },
    postV2ProviderBillingV2ClaimCasesByClaimIdReview: {
      invalidatesTags: (_result, _err, arg) => [
        V2_TAGS.CLAIM_CASES,
        { type: V2_TAGS.CLAIM_CASES, id: arg.claimId },
      ],
    },
    getV2ProviderBillingV2Remittances: {
      providesTags: [V2_TAGS.REMITTANCES],
    },
    getV2ProviderBillingV2PatientBillingQueue: {
      providesTags: [V2_TAGS.PATIENT_BILLING],
    },
    getV2ProviderBillingV2PatientBillingSent: {
      providesTags: [V2_TAGS.PATIENT_BILLING],
    },
    getV2ProviderBillingV2PatientsByPatientIdFinancialSummary: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.PATIENT_DETAIL, id: arg.patientId },
      ],
    },
    postV2ProviderChargeLinesPreview: {
      // Read-only preview — no cache invalidation. The mutation shape is a BE
      // choice (POST so the long ID list goes in the body); we treat it as a
      // query in the wrapper hook.
    },
    getV2ProviderClaimsByClaimIdCertification: {
      providesTags: (_result, _err, arg) => [
        { type: V2_TAGS.CLAIM_CERTIFICATION, id: arg.claimId },
      ],
    },
    postV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyDismiss: {
      invalidatesTags: (result, _err, arg) =>
        result ? [{ type: V2_TAGS.CLAIM_CERTIFICATION, id: arg.claimId }] : [],
    },
    postV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyReopen: {
      invalidatesTags: (result, _err, arg) =>
        result ? [{ type: V2_TAGS.CLAIM_CERTIFICATION, id: arg.claimId }] : [],
    },
  },
});

// The money verbs (gdn-2950 allocation, gdn-2978 refund) come from codegen;
// only the account-history PDF (gdn-2942) is hand-injected, because a Blob
// response has no OpenAPI shape to generate from.
//
// Every money verb refreshes the same billing surfaces the reverse-charge
// verb does (see api.ts), plus this patient's financial summary. One
// over-broad refetch beats a stale balance.
const moneyVerbInvalidation = (patientId: string) => [
  { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
  V2_TAGS.PATIENT_BILLING,
  V2_TAGS.OVERVIEW,
  { type: 'PatientBalance' as const },
  { type: 'PatientInvoices' as const },
  { type: 'BillingEncounter' as const },
  { type: 'BillingEncountersList' as const, id: 'LIST' },
  { type: 'Eligibility' as const },
  { type: 'Worklist' as const },
];

const billingV2ApiWithDocuments = billingV2Api
  .enhanceEndpoints({
    // Cross-slice tags owned by api.ts — re-declared here so this chain's
    // types know them (runtime registration is shared and idempotent).
    addTagTypes: [
      'PatientBalance',
      'PatientInvoices',
      'BillingEncounter',
      'BillingEncountersList',
      'Eligibility',
      'Worklist',
    ],
    endpoints: {
      postV2ProviderBillingV2PatientsByPatientIdCreditAllocations: {
        invalidatesTags: (_result, _error, { patientId }) =>
          moneyVerbInvalidation(patientId),
      },
      // The assignment voids an account charge AND lands credit on an
      // encounter, so it moves money across both scopes — same blast
      // radius as the other money verbs (gdn-3106).
      postV2ProviderBillingV2PatientsByPatientIdChargeAssignments: {
        invalidatesTags: (_result, _error, { patientId }) =>
          moneyVerbInvalidation(patientId),
      },
      postV2ProviderBillingV2PatientsByPatientIdRefunds: {
        invalidatesTags: (_result, _error, { patientId }) =>
          moneyVerbInvalidation(patientId),
      },
      getV2ProviderBillingV2PatientsByPatientIdRefundablePayments: {
        // Rides the patient-detail tag so any money verb refreshes it.
        providesTags: (_result, _error, { patientId }) => [
          { type: V2_TAGS.PATIENT_DETAIL, id: patientId },
        ],
      },

      /**
       * Edit and delete within the author's window (contract v1.5), on the
       * generated endpoints. Both write the notes cache by hand and never
       * invalidate the notes tag: the list on screen is the one the biller is
       * looking at and the patch keeps it right, so a refetch would only make
       * the card flicker. The two failures that need a re-read — a 409 (the
       * note moved elsewhere) and a 404 (it is gone) — are the hook's to ask
       * for. The claim-work rows are another matter: they carry `latest_note`
       * and `work_notes_count`, which both verbs move, so a success refreshes
       * CLAIM_WORK the way an add does; that list is never the card.
       *
       * Both recipes are idempotent under the middleware re-entry described
       * on the add-note endpoint below.
       */
      patchV2ProviderBillingV2EncountersByEncounterIdWorkNotesAndNoteId: {
        /* The new body is on the page the moment it is sent, marked edited;
           the server's note takes its place when the write lands, and a
           refused write puts the old body back — the hook's snackbar says why. */
        async onQueryStarted(
          { encounterId, noteId, encounterWorkNoteEditRequest },
          { dispatch, queryFulfilled },
        ) {
          const shown = dispatch(
            billingV2ApiWithDocuments.util.updateQueryData(
              'encounterWorkNotes',
              { encounterId },
              draft => {
                const note = draft.notes.find(candidate => candidate.id === noteId);
                /* Already the new body: the other pass has been here. */
                if (!note || note.body === encounterWorkNoteEditRequest.body) return;
                note.body = encounterWorkNoteEditRequest.body;
                note.edited_at = new Date().toISOString();
              },
            ),
          );
          try {
            const { data } = await queryFulfilled;
            dispatch(
              billingV2ApiWithDocuments.util.updateQueryData(
                'encounterWorkNotes',
                { encounterId },
                draft => {
                  const at = draft.notes.findIndex(candidate => candidate.id === noteId);
                  /* The schema marks the author fields optional; the server
                     never omits them — see `EncounterWorkNote`. */
                  if (at >= 0) draft.notes[at] = data.note as EncounterWorkNote;
                },
              ),
            );
          } catch {
            shown.undo();
          }
        },
        invalidatesTags: result => (result ? [V2_TAGS.CLAIM_WORK] : []),
      },
      deleteV2ProviderBillingV2EncountersByEncounterIdWorkNotesAndNoteId: {
        /* The note leaves the list as it is sent and comes back only if the
           server refuses. A 204 carries no body, so there is nothing to put
           in its place. */
        async onQueryStarted({ encounterId, noteId }, { dispatch, queryFulfilled }) {
          const shown = dispatch(
            billingV2ApiWithDocuments.util.updateQueryData(
              'encounterWorkNotes',
              { encounterId },
              draft => {
                draft.notes = draft.notes.filter(candidate => candidate.id !== noteId);
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            shown.undo();
          }
        },
        /* The result is `unknown` (a 204 has no body), so the error is what says which. */
        invalidatesTags: (_result, error) => (error ? [] : [V2_TAGS.CLAIM_WORK]),
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      getPatientAccountHistoryPdf: builder.query<Blob, { patientId: string }>({
        query: ({ patientId }) => ({
          url: `/v2/provider/billing/v2/patients/${patientId}/account-history`,
          responseHandler: async (response: Response) => response.blob(),
        }),
      }),

      /**
       * The encounter work verbs (GDN-3380, contract section 6). Hand-injected
       * because the endpoints are not in the OpenAPI spec yet; packet F
       * (GDN-3382) replaces these three with the generated client. The
       * note's PATCH and DELETE (v1.5) already ride the generated
       * endpoints, enhanced above.
       *
       * INVALIDATION, deliberately wide. Archiving an encounter changes what
       * every queue shows and what every badge counts, and the biller is very
       * often looking at a second surface when they do it (Home's queue card
       * while the workbench is open in another tab). The blast radius is one
       * refetch of lists that were going to be refetched on the next
       * navigation anyway; the alternative is a row that is gone from the
       * server and still on screen, which is the bug this feature exists to
       * stop making.
       */
      encounterWorkDisposition: builder.mutation<
        EncounterWorkDispositionResponse,
        { encounterId: string; body: EncounterWorkDispositionRequest }
      >({
        query: ({ encounterId, body }) => ({
          url: `/v2/provider/billing/v2/encounters/${encounterId}/work-disposition`,
          method: 'POST',
          body,
        }),
        /**
         * NOTHING ON A FAILURE. RTK Query runs `invalidatesTags` for errors
         * too unless the function says otherwise, and a write the server
         * refused changed nothing — refetching five surfaces after a 500 is
         * pure noise on top of an outage. The one failure that DOES need a
         * re-read is a 409, where the server holds a version this client does
         * not; the hook invalidates explicitly for that case, because it is
         * the only place that can tell a conflict from an outage.
         */
        invalidatesTags: (result, _error, { encounterId }) =>
          result
            ? [
                V2_TAGS.CLAIM_WORK,
                V2_TAGS.CLAIM_WORK_COUNTS,
                { type: V2_TAGS.ENCOUNTER_WORK_NOTES, id: encounterId },
                { type: 'BillingEncounter' as const, id: encounterId },
                { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
              ]
            : [],
      }),

      addEncounterWorkNote: builder.mutation<
        EncounterWorkNoteResponse,
        { encounterId: string; body: EncounterWorkNoteRequest }
      >({
        query: ({ encounterId, body }) => ({
          url: `/v2/provider/billing/v2/encounters/${encounterId}/work-notes`,
          method: 'POST',
          body,
        }),
        /* The note is on the page the moment it is sent, as a provisional
           entry under the signed-in name; the server's own note takes its
           place when the write lands, and a refused write takes it away
           again — the hook's snackbar says so. */
        async onQueryStarted({ encounterId, body }, lifecycle) {
          const { dispatch, queryFulfilled, requestId } = lifecycle;
          const state = lifecycle.getState() as RootState;
          /**
           * EVERY RECIPE IN THIS FILE IS IDEMPOTENT. The app's dynamic
           * middleware re-enters RTK Query's chain (see the note in
           * `server-ui/store/api.ts`), so this lifecycle runs TWICE for one
           * request, each pass with its own patch and undo. A pass that finds
           * its work already done by the other changes nothing, so it has
           * nothing to undo either; without that, a note sent once showed
           * twice until the refetch, and a failed edit rolled back to a body
           * still marked "edited".
           *
           * The two passes are told apart from two REQUESTS by the request
           * id, which both passes share: the provisional note is named by it,
           * so a second note with the same words sent before the first lands
           * gets a provisional of its own.
           */
          const provisional = makeProvisionalNote({
            body: body.body,
            requestId,
            authorName: selectSignedInName(state),
            authorPrincipalId: selectSignedInPrincipalId(state),
            claimId: body.claim_id ?? null,
          });
          const shown = dispatch(
            billingV2ApiWithDocuments.util.updateQueryData(
              'encounterWorkNotes',
              { encounterId },
              draft => {
                if (draft.notes.some(note => note.id === provisional.id)) return;
                draft.notes.unshift(provisional);
              },
            ),
          );
          try {
            const { data } = await queryFulfilled;
            dispatch(
              billingV2ApiWithDocuments.util.updateQueryData(
                'encounterWorkNotes',
                { encounterId },
                draft => {
                  /* The first pass to get here replaces it; the other finds
                     nothing left to do. */
                  const at = draft.notes.findIndex(note => note.id === provisional.id);
                  if (at >= 0) draft.notes[at] = data.note;
                },
              ),
            );
          } catch {
            shown.undo();
          }
        },
        /* A note never changes the disposition (contract section 6), so it
           refreshes the notes and the rows that show `latest_note`, and
           nothing else. Counts cannot move. */
        /* Same rule: a note the server did not take is not a reason to
           refetch the rows that would have shown it. */
        invalidatesTags: (result, _error, { encounterId }) =>
          result
            ? [V2_TAGS.CLAIM_WORK, { type: V2_TAGS.ENCOUNTER_WORK_NOTES, id: encounterId }]
            : [],
      }),

      encounterWorkNotes: builder.query<EncounterWorkNotesResponse, { encounterId: string }>({
        query: ({ encounterId }) => ({
          url: `/v2/provider/billing/v2/encounters/${encounterId}/work-notes`,
        }),
        providesTags: (_result, _error, { encounterId }) => [
          { type: V2_TAGS.ENCOUNTER_WORK_NOTES, id: encounterId },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useLazyGetPatientAccountHistoryPdfQuery: useLazyAccountHistoryPdfQuery,
  useEncounterWorkDispositionMutation,
  useAddEncounterWorkNoteMutation,
  usePatchV2ProviderBillingV2EncountersByEncounterIdWorkNotesAndNoteIdMutation:
    useEditEncounterWorkNoteMutation,
  useDeleteV2ProviderBillingV2EncountersByEncounterIdWorkNotesAndNoteIdMutation:
    useDeleteEncounterWorkNoteMutation,
  useEncounterWorkNotesQuery,
  usePostV2ProviderBillingV2PatientsByPatientIdCreditAllocationsMutation:
    useCreditAllocationMutation,
  usePostV2ProviderBillingV2PatientsByPatientIdChargeAssignmentsMutation:
    useAssignChargeToEncounterMutation,
  useGetV2ProviderBillingV2PatientsByPatientIdRefundablePaymentsQuery:
    useRefundablePaymentsQuery,
  usePostV2ProviderBillingV2PatientsByPatientIdRefundsMutation:
    usePatientRefundMutation,
} = billingV2ApiWithDocuments;

// Export user-friendly hooks
export const {
  useGetV2ProviderBillingV2OverviewQuery: useOverviewQuery,
  useGetV2ProviderBillingV2WorkQuery: useWorkQueueQuery,
  useGetV2ProviderBillingV2ClaimsQuery: useClaimsV2Query,
  useGetV2ProviderBillingV2ClaimWorkQuery: useClaimWorkQuery,
  useGetV2ProviderBillingV2ClaimWorkSavedViewsQuery: useClaimWorkSavedViewsQuery,
  usePostV2ProviderBillingV2ClaimWorkSavedViewsMutation: useCreateClaimWorkSavedViewMutation,
  usePutV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewIdMutation:
    useUpdateClaimWorkSavedViewMutation,
  useDeleteV2ProviderBillingV2ClaimWorkSavedViewsBySavedViewIdMutation:
    useDeleteClaimWorkSavedViewMutation,
  useGetV2ProviderBillingV2ClaimCasesQuery: useClaimCasesQuery,
  useGetV2ProviderBillingV2ClaimCasesByClaimIdQuery: useClaimCaseDetailQuery,
  usePostV2ProviderBillingV2ClaimCasesByClaimIdReviewMutation: useGenerateClaimCaseReviewMutation,
  useGetV2ProviderBillingV2RemittancesQuery: useRemittancesV2Query,
  useGetV2ProviderBillingV2PatientBillingQueueQuery: usePatientBillingQueueQuery,
  useGetV2ProviderBillingV2PatientBillingSentQuery: usePatientBillingSentQuery,
  useGetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryQuery: usePatientFinancialSummaryQuery,
  usePostV2ProviderChargeLinesPreviewMutation: useChargeLinesPreviewMutation,
  useGetV2ProviderBillingV2PayerOptionsQuery: useBillingPayerOptionsQuery,
  useGetV2ProviderClaimsByClaimIdCertificationQuery: useClaimCertificationQuery,
  usePostV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyDismissMutation:
    useDismissClaimCertificationFindingMutation,
  usePostV2ProviderClaimsByClaimIdCertificationFindingsAndFindingKeyReopenMutation:
    useReopenClaimCertificationFindingMutation,
} = billingV2Api;

export { billingV2Api, V2_TAGS };
